import type { DragEvent, MutableRefObject } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Film,
  RotateCcw,
  Trash2,
  Upload,
  X,
  XCircle,
} from "lucide-react";

import { Auth } from "@lib/authentication";
import { parseAuthResult } from "@lib/authentication/amplify/amplify.helpers";

import {
  basenameCollisions,
  basenameFromFilename,
  DEFAULT_MODE,
  type Mode,
  needsPublicConfirmation,
  type PriorPublish,
  priorPublishCollisions,
  PUBLIC_DESTINATION_NOTE,
  publishPubliclyLabel,
  requiresPublicConfirmation,
} from "./uploadModes";

const API_URL = import.meta.env.VITE_VIDEO_ENCODER_API_URL;
const AWS_REGION = "us-west-1";
const OUTPUT_BUCKET = "omgyes.episode-video-output";
const LEAFLET_BUCKET = "fgs-public-video-322407519684-us-west-1-an";
const PUBLIC_VIDEO_BASE_URL = "https://videos.omgyes.com";
const LEAFLET_VIDEO_BASE_URL = "https://d2hb3ue6liy8zx.cloudfront.net";
const RECENT_UPLOADS_KEY = "omgyes-video-encoder-recent-uploads";
const RECENT_UPLOADS_MAX = 50;
// Sources auto-expire from the upload bucket after 30 days, so links to them
// would 404. Prune local history at the same TTL.
const RECENT_UPLOADS_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

// How many uploads run in parallel. Higher saturates uplink and can starve
// retries; 3 has been a reliable sweet spot for batch upload UIs.
const CONCURRENCY = 3;

// Files beyond this trigger a soft warning. No hard cap.
const LARGE_BATCH_THRESHOLD = 25;

// Per-step retry budget for transient errors (network, 5xx).
const MAX_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 1000;

interface ModeOption {
  value: Mode;
  label: string;
  description: string;
  group: "episode" | "marketing";
}

const MODES: ModeOption[] = [
  {
    value: "default",
    label: "Default",
    description: "Explicit, private. Lands at videos/<basename>/",
    group: "episode",
  },
  {
    value: "public",
    label: "Public",
    description: "Explicit, public. Lands at videos/public/<basename>/",
    group: "episode",
  },
  {
    value: "non-explicit",
    label: "Non-explicit",
    description:
      "Non-explicit, private. Lands at non-explicit/videos/<basename>/",
    group: "episode",
  },
  {
    value: "non-explicit-public",
    label: "Non-explicit + Public",
    description:
      "Non-explicit, public. Lands at non-explicit/videos/public/<basename>/",
    group: "episode",
  },
  {
    value: "leaflet",
    label: "Leaflet",
    description:
      "Marketing splash-page video only, never member footage. Same encoding pipeline; output goes to the leaflet CloudFront-fronted bucket as videos/<basename>/.",
    group: "marketing",
  },
];

function bucketForMode(mode: Mode): string {
  return mode === "leaflet" ? LEAFLET_BUCKET : OUTPUT_BUCKET;
}

function publicBaseUrlForMode(mode: Mode): string {
  return mode === "leaflet" ? LEAFLET_VIDEO_BASE_URL : PUBLIC_VIDEO_BASE_URL;
}

interface PresignResponse {
  uploadUrl: string;
  bucket: string;
  key: string;
}

type FileStatus =
  | { stage: "queued" }
  | { stage: "presigning"; attempt: number }
  | { stage: "uploading"; pct: number; xhr: XMLHttpRequest; attempt: number }
  | {
      stage: "done";
      bucket: string;
      key: string;
      outputBucket: string;
      outputPrefix: string;
      finalUrl: string;
    }
  | { stage: "error"; message: string; canRetry: boolean };

interface FileEntry {
  id: string;
  file: File;
  status: FileStatus;
}

// A public-CDN upload waiting on the operator. `files` is a snapshot of what
// the dialog lists, and confirming runs exactly those entries in `mode`.
interface PendingPublicPublish {
  mode: Mode;
  files: { id: string; name: string; size: number }[];
}

interface RecentUpload {
  id: string;
  filename: string;
  size: number;
  mode: Mode;
  uploadedAt: number; // epoch ms
  bucket: string;
  key: string;
  outputBucket: string;
  outputPrefix: string;
  finalUrl: string;
}

function pruneRecentUploads(uploads: RecentUpload[]): RecentUpload[] {
  const cutoff = Date.now() - RECENT_UPLOADS_MAX_AGE_MS;
  return uploads
    .filter((u) => typeof u.uploadedAt === "number" && u.uploadedAt >= cutoff)
    .slice(0, RECENT_UPLOADS_MAX);
}

function loadRecentUploads(): RecentUpload[] {
  try {
    const raw = localStorage.getItem(RECENT_UPLOADS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return pruneRecentUploads(parsed);
  } catch {
    return [];
  }
}

function saveRecentUploads(uploads: RecentUpload[]): void {
  try {
    localStorage.setItem(
      RECENT_UPLOADS_KEY,
      JSON.stringify(pruneRecentUploads(uploads)),
    );
  } catch {
    // localStorage full or disabled — silently fail. Recents are nice-to-have.
  }
}

function formatRelativeTime(epochMs: number): string {
  const diffSec = Math.max(0, Math.floor((Date.now() - epochMs) / 1000));
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
}

function estimatedEncodingDoneAt(
  uploadedAt: number,
  sizeBytes: number,
): number {
  // Very rough: 1MB per second of source video at ~12 minutes of wall-time per
  // 2-minute video. For batches, parallelism means the user's perceived
  // "done" time is roughly the same regardless of count.
  const baseMs = 12 * 60 * 1000;
  const perGbBonus = (sizeBytes / (1024 * 1024 * 1024)) * 4 * 60 * 1000;
  return uploadedAt + baseMs + perGbBonus;
}

// ---------- helpers ----------

function s3SourceConsoleUrl(bucket: string, key: string): string {
  return `https://${AWS_REGION}.console.aws.amazon.com/s3/object/${bucket}?region=${AWS_REGION}&prefix=${encodeURIComponent(key)}`;
}

function s3PrefixConsoleUrl(bucket: string, prefix: string): string {
  return `https://${AWS_REGION}.console.aws.amazon.com/s3/buckets/${bucket}?region=${AWS_REGION}&prefix=${encodeURIComponent(prefix)}&showversions=false`;
}

function outputPrefixForKey(key: string): string {
  const parts = key.split("/");
  parts.shift();
  const filename = parts.pop() || "";
  const basename = basenameFromFilename(filename);
  const modePath = parts.join("/");
  // Leaflet writes to a separate bucket entirely; output layout there is just
  // `videos/<basename>/` (no leaflet-prefix within the bucket).
  if (modePath === "leaflet") {
    return `videos/${basename}/`;
  }
  const hasNonExplicit = modePath.startsWith("non-explicit");
  const isPublic = modePath.endsWith("public");
  const root = hasNonExplicit ? "non-explicit/videos" : "videos";
  const pub = isPublic ? "public/" : "";
  return `${root}/${pub}${basename}/`;
}

function finalUrlForKey(key: string, mode: Mode): string {
  const base = publicBaseUrlForMode(mode);
  const prefix = outputPrefixForKey(key);
  // Leaflet splash pages don't have an HLS player yet — they use the largest
  // base MP4 rendition directly. The encoder produces this at the same time
  // as the HLS chunks, so no pipeline change needed; we just point users at
  // the right file. When leaflet adopts HLS, switch this back to playlist.m3u8.
  if (mode === "leaflet") {
    return `${base}/${prefix}large-1920-1080.mp4`;
  }
  return `${base}/${prefix}playlist.m3u8`;
}

function finalUrlLabel(url: string): string {
  return url.endsWith(".mp4")
    ? "Final URL (1080p MP4)"
    : "Final URL (HLS playlist)";
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function getAuthToken(): Promise<string> {
  const { accessToken } = parseAuthResult(
    await Auth.currentAuthenticatedUser(),
  );
  if (!accessToken?.jwt) {
    throw new Error("Not authenticated");
  }
  return accessToken.jwt;
}

class HttpError extends Error {
  status: number;
  retryable: boolean;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    // 5xx + 408/429 = transient. 4xx (other) = permanent client error.
    this.retryable = status >= 500 || status === 408 || status === 429;
  }
}

async function requestPresignedUrl(
  filename: string,
  mode: Mode,
): Promise<PresignResponse> {
  if (!API_URL) {
    throw new Error("VITE_VIDEO_ENCODER_API_URL is not configured");
  }
  const token = await getAuthToken();
  const response = await fetch(`${API_URL}/upload-url`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ filename, mode }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new HttpError(
      response.status,
      `Presign failed: ${response.status} ${text}`,
    );
  }
  return response.json();
}

function uploadWithProgress(
  url: string,
  file: File,
  onStart: (xhr: XMLHttpRequest) => void,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    onStart(xhr);
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        const err = new HttpError(
          xhr.status,
          `S3 upload failed: ${xhr.status} ${xhr.responseText}`,
        );
        reject(err);
      }
    });
    xhr.addEventListener("error", () => {
      // Network error: treat as retryable.
      const err = new HttpError(0, "Network error during upload");
      err.retryable = true;
      reject(err);
    });
    xhr.addEventListener("abort", () => {
      const err = new Error("Upload aborted");
      (err as Error & { aborted?: boolean }).aborted = true;
      reject(err);
    });
    xhr.send(file);
  });
}

interface ProcessingHandles {
  abortedRef: MutableRefObject<boolean>;
  updateStatus: (id: string, next: FileStatus) => void;
  recordDone: (upload: RecentUpload) => void;
}

// Allowed video extensions, matching the backend's FILENAME_PATTERN. Drag-drop
// doesn't honor <input accept>, so we filter here too.
const VIDEO_EXTENSION_PATTERN = /\.(mp4|mov|m4v|mkv|avi|webm)$/i;

async function processOne(
  entry: FileEntry,
  mode: Mode,
  handles: ProcessingHandles,
): Promise<void> {
  const { abortedRef, updateStatus, recordDone } = handles;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    if (abortedRef.current) return;
    try {
      updateStatus(entry.id, { stage: "presigning", attempt });
      const presigned = await requestPresignedUrl(entry.file.name, mode);
      if (abortedRef.current) return;

      let activeXhr: XMLHttpRequest | null = null;
      await uploadWithProgress(
        presigned.uploadUrl,
        entry.file,
        (xhr) => {
          activeXhr = xhr;
          updateStatus(entry.id, { stage: "uploading", pct: 0, xhr, attempt });
        },
        (pct) => {
          if (activeXhr && !abortedRef.current) {
            updateStatus(entry.id, {
              stage: "uploading",
              pct,
              xhr: activeXhr,
              attempt,
            });
          }
        },
      );

      const outputBucket = bucketForMode(mode);
      const outputPrefix = outputPrefixForKey(presigned.key);
      const finalUrl = finalUrlForKey(presigned.key, mode);
      updateStatus(entry.id, {
        stage: "done",
        bucket: presigned.bucket,
        key: presigned.key,
        outputBucket,
        outputPrefix,
        finalUrl,
      });
      // Side-effecting record happens here (not inside the setState updater)
      // so it fires exactly once per successful upload.
      recordDone({
        id: entry.id,
        filename: entry.file.name,
        size: entry.file.size,
        mode,
        uploadedAt: Date.now(),
        bucket: presigned.bucket,
        key: presigned.key,
        outputBucket,
        outputPrefix,
        finalUrl,
      });
      return;
    } catch (err) {
      if (abortedRef.current) return;
      if ((err as { aborted?: boolean }).aborted) return;

      const isHttp = err instanceof HttpError;
      const retryable = isHttp ? err.retryable : true;
      const isLast = attempt === MAX_ATTEMPTS;
      if (!retryable || isLast) {
        updateStatus(entry.id, {
          stage: "error",
          message: err instanceof Error ? err.message : String(err),
          canRetry: !isHttp || err.retryable,
        });
        return;
      }
      const backoff = RETRY_BASE_DELAY_MS * 2 ** (attempt - 1);
      await sleep(backoff);
    }
  }
}

// Run `processOne` over all entries with bounded concurrency.
async function runBatch(
  entries: FileEntry[],
  mode: Mode,
  handles: ProcessingHandles,
  concurrency: number,
): Promise<void> {
  let cursor = 0;
  const workers = Array.from(
    { length: Math.min(concurrency, entries.length) },
    async () => {
      while (cursor < entries.length) {
        const idx = cursor++;
        if (handles.abortedRef.current) return;
        await processOne(entries[idx], mode, handles);
      }
    },
  );
  await Promise.all(workers);
}

// ---------- component ----------

export function UploadVideo() {
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [mode, setMode] = useState<Mode>(DEFAULT_MODE);
  const [pendingPublicPublish, setPendingPublicPublish] =
    useState<PendingPublicPublish | null>(null);
  // Entry ids the operator confirmed for the public CDN in the current mode.
  // Cleared on any mode change, so returning to leaflet asks again.
  const publicConfirmedIdsRef = useRef<Set<string>>(new Set());
  const [isRunning, setIsRunning] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [recentUploads, setRecentUploads] = useState<RecentUpload[]>(() =>
    loadRecentUploads(),
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortedRef = useRef(false);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  // Re-render relative timestamps periodically.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (recentUploads.length === 0) return;
    const interval = window.setInterval(() => setTick((t) => t + 1), 30_000);
    return () => window.clearInterval(interval);
  }, [recentUploads.length]);

  const recordRecentUpload = useCallback((upload: RecentUpload) => {
    setRecentUploads((prev) => {
      const next = [upload, ...prev].slice(0, RECENT_UPLOADS_MAX);
      saveRecentUploads(next);
      return next;
    });
  }, []);

  const removeRecentUpload = useCallback((id: string) => {
    setRecentUploads((prev) => {
      const next = prev.filter((u) => u.id !== id);
      saveRecentUploads(next);
      return next;
    });
  }, []);

  const clearRecentUploads = useCallback(() => {
    setRecentUploads([]);
    saveRecentUploads([]);
  }, []);

  const updateStatus = useCallback((id: string, next: FileStatus) => {
    // Pure: just maps prev -> next. The recent-uploads side effect is
    // handled in processOne (recordDone) so we don't fire it twice if React
    // invokes this updater more than once (strict mode / concurrent rendering).
    setEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, status: next } : e)),
    );
  }, []);

  // Warn user before navigating away while a batch is running.
  useEffect(() => {
    if (!isRunning) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Most browsers ignore the message string and show their own.
      e.returnValue =
        "Uploads are still in progress. Leaving will cancel them.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isRunning]);

  const [rejectedCount, setRejectedCount] = useState(0);

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    setHasRun(false);
    const all = Array.from(newFiles);
    // Drag-drop ignores <input accept>, so we filter here to match the backend
    // FILENAME_PATTERN. Picker uses the same accept attribute as a hint.
    const accepted = all.filter((f) => VIDEO_EXTENSION_PATTERN.test(f.name));
    const rejected = all.length - accepted.length;
    if (rejected > 0) setRejectedCount(rejected);
    const additions = accepted.map<FileEntry>((file) => ({
      id: newId(),
      file,
      status: { stage: "queued" },
    }));
    setEntries((prev) => {
      // Dedupe by name+size against existing queued/error entries.
      const seen = new Set(
        prev
          .filter(
            (e) => e.status.stage === "queued" || e.status.stage === "error",
          )
          .map((e) => `${e.file.name}::${e.file.size}`),
      );
      return [
        ...prev,
        ...additions.filter((a) => !seen.has(`${a.file.name}::${a.file.size}`)),
      ];
    });
  }, []);

  const removeEntry = useCallback((id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const clearCompleted = useCallback(() => {
    setEntries((prev) => prev.filter((e) => e.status.stage !== "done"));
  }, []);

  const clearAll = useCallback(() => {
    setEntries([]);
    setHasRun(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const selectMode = useCallback((next: Mode) => {
    publicConfirmedIdsRef.current.clear();
    setPendingPublicPublish(null);
    setMode(next);
  }, []);

  // Runs the given entries (those still queued or failed) in `runMode`. Only
  // reached through requestRun or confirmPublicPublish, never directly.
  const runEntries = useCallback(
    async (ids: string[], runMode: Mode) => {
      const idSet = new Set(ids);
      const toProcess = entriesRef.current.filter(
        (e) =>
          idSet.has(e.id) &&
          (e.status.stage === "queued" || e.status.stage === "error"),
      );
      if (toProcess.length === 0) return;
      // Reset retried entries to queued state before running.
      setEntries((prev) =>
        prev.map((e) =>
          idSet.has(e.id) && e.status.stage === "error"
            ? { ...e, status: { stage: "queued" } }
            : e,
        ),
      );
      abortedRef.current = false;
      setIsRunning(true);
      setHasRun(true);
      try {
        await runBatch(
          toProcess,
          runMode,
          { abortedRef, updateStatus, recordDone: recordRecentUpload },
          CONCURRENCY,
        );
      } finally {
        setIsRunning(false);
      }
    },
    [updateStatus, recordRecentUpload],
  );

  // The single gate in front of every upload. A public-CDN mode with any entry
  // not yet confirmed opens the dialog instead of presigning; every other mode
  // runs straight away, as before.
  const requestRun = useCallback(
    (ids: string[]) => {
      if (needsPublicConfirmation(mode, ids, publicConfirmedIdsRef.current)) {
        const idSet = new Set(ids);
        setPendingPublicPublish({
          mode,
          files: entriesRef.current
            .filter((e) => idSet.has(e.id))
            .map((e) => ({ id: e.id, name: e.file.name, size: e.file.size })),
        });
        return;
      }
      void runEntries(ids, mode);
    },
    [mode, runEntries],
  );

  const confirmPublicPublish = useCallback(() => {
    if (!pendingPublicPublish) return;
    const ids = pendingPublicPublish.files.map((f) => f.id);
    ids.forEach((id) => publicConfirmedIdsRef.current.add(id));
    setPendingPublicPublish(null);
    void runEntries(ids, pendingPublicPublish.mode);
  }, [pendingPublicPublish, runEntries]);

  const cancelPublicPublish = useCallback(() => {
    setPendingPublicPublish(null);
  }, []);

  const startBatch = useCallback(() => {
    const ids = entriesRef.current
      .filter((e) => e.status.stage === "queued" || e.status.stage === "error")
      .map((e) => e.id);
    if (ids.length === 0) return;
    requestRun(ids);
  }, [requestRun]);

  const cancelBatch = useCallback(() => {
    abortedRef.current = true;
    // Abort in-flight XHRs.
    entriesRef.current.forEach((e) => {
      if (e.status.stage === "uploading") {
        e.status.xhr.abort();
      }
    });
    // Mark queued + presigning entries as cancelled.
    setEntries((prev) =>
      prev.map((e) => {
        if (e.status.stage === "queued" || e.status.stage === "presigning") {
          return {
            ...e,
            status: { stage: "error", message: "Cancelled", canRetry: true },
          };
        }
        if (e.status.stage === "uploading") {
          return {
            ...e,
            status: { stage: "error", message: "Cancelled", canRetry: true },
          };
        }
        return e;
      }),
    );
  }, []);

  const retryEntry = useCallback(
    (id: string) => {
      const entry = entriesRef.current.find((e) => e.id === id);
      if (!entry || entry.status.stage !== "error") return;
      // A single-entry batch, through the same gate as Start upload.
      requestRun([id]);
    },
    [requestRun],
  );

  const onDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);
  const onDragLeave = useCallback(() => setDragOver(false), []);
  const onDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const counts = useMemo(() => {
    let queued = 0,
      inFlight = 0,
      done = 0,
      error = 0;
    for (const e of entries) {
      switch (e.status.stage) {
        case "queued":
          queued++;
          break;
        case "presigning":
        case "uploading":
          inFlight++;
          break;
        case "done":
          done++;
          break;
        case "error":
          error++;
          break;
      }
    }
    return { queued, inFlight, done, error, total: entries.length };
  }, [entries]);

  const totalBytes = useMemo(
    () => entries.reduce((acc, e) => acc + e.file.size, 0),
    [entries],
  );

  const hasPending = counts.queued > 0 || counts.error > 0;
  const allDone =
    hasRun &&
    !isRunning &&
    counts.total > 0 &&
    counts.queued === 0 &&
    counts.inFlight === 0;
  const showLargeBatchWarning =
    entries.length >= LARGE_BATCH_THRESHOLD && !isRunning && counts.done === 0;

  return (
    <div className="max-w-3xl mx-auto py-6">
      <header className="mb-6 flex items-center gap-3">
        <Film className="w-7 h-7 text-gray-700" />
        <div>
          <h1 className="text-2xl font-semibold">Upload videos</h1>
          <p className="text-sm text-gray-600">
            Pick or drag multiple video files. Each will be encoded into HLS +
            base MP4 renditions and published to S3. Typical encode time: 10–20
            minutes per video, in parallel.
          </p>
        </div>
      </header>

      <section className="mb-6">
        <h2 className="text-sm font-medium text-gray-700 mb-2">Mode</h2>
        <ModeGroup
          title="Episode videos"
          modes={MODES.filter((m) => m.group === "episode")}
          selected={mode}
          onSelect={selectMode}
          disabled={isRunning || pendingPublicPublish !== null}
        />
        <ModeGroup
          title="Marketing"
          modes={MODES.filter((m) => m.group === "marketing")}
          selected={mode}
          onSelect={selectMode}
          disabled={isRunning || pendingPublicPublish !== null}
          className="mt-3"
        />
        <p className="text-xs text-gray-500 mt-2">
          Mode applies to all files in this batch. To upload some files with
          different modes, run them as separate batches.
        </p>
      </section>

      <section
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`mb-4 border-2 border-dashed rounded-lg p-6 text-center ${
          dragOver ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-white"
        } ${isRunning ? "opacity-50" : ""}`}
      >
        <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
        <p className="text-gray-600 mb-1">Drag and drop video files, or</p>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isRunning}
          className="text-blue-600 hover:underline font-medium disabled:text-gray-400"
        >
          click to choose files
        </button>
        <p className="text-xs text-gray-400 mt-2">
          Accepts: .mp4, .mov, .m4v, .mkv, .avi, .webm · up to 5 GB each
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*,.mp4,.mov,.m4v,.mkv,.avi,.webm"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) addFiles(e.target.files);
            // Reset input so re-selecting the same file works.
            if (e.target) e.target.value = "";
          }}
        />
      </section>

      {rejectedCount > 0 && (
        <div className="mb-4 flex gap-2 items-start text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
          <AlertTriangle className="w-5 h-5 flex-none mt-0.5" />
          <div className="text-sm flex-1">
            Skipped {rejectedCount} file{rejectedCount === 1 ? "" : "s"} that
            don't have a supported video extension (.mp4, .mov, .m4v, .mkv,
            .avi, .webm).
          </div>
          <button
            type="button"
            onClick={() => setRejectedCount(0)}
            className="text-amber-900 hover:underline text-sm flex-none"
          >
            Dismiss
          </button>
        </div>
      )}

      {showLargeBatchWarning && (
        <div className="mb-4 flex gap-2 items-start text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
          <AlertTriangle className="w-5 h-5 flex-none mt-0.5" />
          <div className="text-sm">
            <strong>
              Big batch ({entries.length} files, {formatBytes(totalBytes)}{" "}
              total).
            </strong>{" "}
            Uploads run {CONCURRENCY} at a time and can take a while. Keep this
            tab open until the batch finishes — closing it will cancel any
            in-flight uploads.
          </div>
        </div>
      )}

      {entries.length > 0 && (
        <section className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <div className="text-sm font-medium text-gray-700">
              {entries.length} file{entries.length === 1 ? "" : "s"} ·{" "}
              {formatBytes(totalBytes)}
            </div>
            {!isRunning && (
              <div className="flex gap-3 text-sm">
                {counts.done > 0 && (
                  <button
                    type="button"
                    onClick={clearCompleted}
                    className="text-gray-600 hover:underline"
                  >
                    Clear completed
                  </button>
                )}
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-gray-600 hover:underline"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
          <ul className="border border-gray-200 rounded divide-y divide-gray-200 bg-white">
            {entries.map((entry) => (
              <FileRow
                key={entry.id}
                entry={entry}
                isRunning={isRunning}
                onRemove={() => removeEntry(entry.id)}
                onRetry={() => retryEntry(entry.id)}
              />
            ))}
          </ul>
        </section>
      )}

      {allDone && (counts.done > 0 || counts.error > 0) && (
        <div
          className={`mb-4 rounded p-3 border text-sm ${
            counts.error === 0
              ? "bg-green-50 border-green-200 text-green-900"
              : "bg-amber-50 border-amber-200 text-amber-900"
          }`}
        >
          <div>
            <strong>Upload complete.</strong> {counts.done} uploaded
            {counts.error > 0 ? `, ${counts.error} failed` : ""}.
            {counts.error > 0 && (
              <>
                {" "}
                Click the retry button next to each failed file to try again.
              </>
            )}
          </div>
          {counts.done > 0 && (
            <div className="mt-1.5 flex gap-1.5 items-start">
              <Clock className="w-4 h-4 flex-none mt-0.5" />
              <div>
                <strong>Encoding still in progress.</strong> Uploaded files are
                now being encoded on Fargate — this takes another ~10–20 minutes
                per video (running in parallel). Output won't appear in S3 or at
                the final URL until encoding finishes. Check back later, or
                scroll down to see your recent uploads.
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={startBatch}
          disabled={!hasPending || isRunning}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-medium px-4 py-2 rounded"
        >
          {isRunning
            ? `Uploading… (${counts.done + counts.error}/${counts.total})`
            : counts.error > 0 && counts.queued === 0
              ? `Retry ${counts.error} failed`
              : `Start upload (${counts.queued} file${counts.queued === 1 ? "" : "s"})`}
        </button>
        {isRunning && (
          <button
            type="button"
            onClick={cancelBatch}
            className="border border-gray-300 hover:bg-gray-100 px-4 py-2 rounded"
          >
            Cancel batch
          </button>
        )}
      </div>

      {recentUploads.length > 0 && (
        <RecentUploadsPanel
          uploads={recentUploads}
          onRemove={removeRecentUpload}
          onClearAll={clearRecentUploads}
        />
      )}

      {pendingPublicPublish && (
        <PublicPublishDialog
          mode={pendingPublicPublish.mode}
          files={pendingPublicPublish.files}
          priorPublishes={recentUploads}
          onConfirm={confirmPublicPublish}
          onCancel={cancelPublicPublish}
        />
      )}
    </div>
  );
}

// Confirmation before anything is presigned in a public-CDN mode (OMG-2352).
// Same shape as ConfirmDialog in pages/v2/User.tsx, plus the file list: the
// point is that the operator reads the filenames before confirming. Cancel has
// focus, so Enter, Escape and a backdrop click all back out.
function PublicPublishDialog({
  mode,
  files,
  priorPublishes,
  onConfirm,
  onCancel,
}: {
  mode: Mode;
  files: PendingPublicPublish["files"];
  priorPublishes: PriorPublish[];
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  const collisions = basenameCollisions(files.map((f) => f.name));
  const replacements = priorPublishCollisions(
    files.map((f) => f.name),
    priorPublishes,
  );
  const count = files.length;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="public-publish-title"
        aria-describedby="public-publish-destination"
        className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl"
      >
        <h3
          id="public-publish-title"
          className="text-lg font-semibold mb-2 flex items-center gap-2 text-red-800"
        >
          <AlertTriangle className="w-5 h-5 flex-none" />
          Publish to the public marketing CDN?
        </h3>
        <p className="text-sm text-gray-700">
          About to publish {count} file{count === 1 ? "" : "s"} via {mode} mode.
        </p>
        <p id="public-publish-destination" className="text-sm text-gray-700">
          <span className="font-medium">Destination:</span>{" "}
          <span className="text-red-800 font-medium">
            {PUBLIC_DESTINATION_NOTE}
          </span>
          .
        </p>
        <p className="text-sm text-gray-700 mt-2 mb-3">
          Once encoding finishes, anyone with the link can watch these. Check
          that every file below is marketing footage, never member footage.
        </p>
        <ul className="max-h-60 overflow-y-auto border border-gray-200 rounded divide-y divide-gray-200 mb-3">
          {files.map((f) => (
            <li
              key={f.id}
              className="px-3 py-1.5 text-sm flex justify-between gap-3"
            >
              <span className="font-mono break-all">{f.name}</span>
              <span className="text-gray-500 flex-none">
                {formatBytes(f.size)}
              </span>
            </li>
          ))}
        </ul>
        {collisions.length > 0 && (
          <div className="mb-3 flex gap-2 items-start text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 text-sm">
            <AlertTriangle className="w-5 h-5 flex-none mt-0.5" />
            <div>
              {collisions.map((c) => (
                <div key={c.basename}>
                  {c.filenames.join(", ")} all publish to videos/{c.basename}/,
                  so they overwrite each other.
                </div>
              ))}
            </div>
          </div>
        )}
        {replacements.length > 0 && (
          <div className="mb-3 flex gap-2 items-start text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 text-sm">
            <AlertTriangle className="w-5 h-5 flex-none mt-0.5" />
            <div>
              {replacements.map((r, i) => (
                <div key={`${r.filename}-${i}`}>
                  {r.filename} replaces the live videos/{r.basename}/ (published
                  from this browser as {r.previous.filename},{" "}
                  {formatRelativeTime(r.previous.uploadedAt)}).
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            autoFocus
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
          >
            {publishPubliclyLabel(count)}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModeGroup({
  title,
  modes,
  selected,
  onSelect,
  disabled,
  className,
}: {
  title: string;
  modes: ModeOption[];
  selected: Mode;
  onSelect: (m: Mode) => void;
  disabled: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
        {title}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {modes.map((m) => {
          const isPublicCdn = requiresPublicConfirmation(m.value);
          const isSelected = selected === m.value;
          // Public-CDN modes stay red whether or not they are selected, so
          // they never read as just another episode option.
          const tone = isPublicCdn
            ? isSelected
              ? "border-red-500 bg-red-50"
              : "border-red-200 bg-white"
            : isSelected
              ? "border-blue-500 bg-blue-50"
              : "border-gray-200 bg-white";
          return (
            <label
              key={m.value}
              className={`flex gap-3 p-3 rounded border cursor-pointer ${tone} ${
                disabled ? "opacity-50 pointer-events-none" : ""
              }`}
            >
              <input
                type="radio"
                name="mode"
                value={m.value}
                checked={isSelected}
                onChange={() => onSelect(m.value)}
                disabled={disabled}
                className="mt-1"
              />
              <div>
                <div className="font-medium flex items-center gap-2">
                  {m.label}
                  {isPublicCdn && (
                    <span className="text-xs font-semibold uppercase tracking-wide bg-red-100 text-red-800 px-1.5 py-0.5 rounded">
                      Public CDN
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500">{m.description}</div>
                {isPublicCdn && (
                  <div className="text-xs text-red-700 mt-1">
                    {PUBLIC_DESTINATION_NOTE}. You will confirm the file list
                    before anything uploads.
                  </div>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function RecentUploadsPanel({
  uploads,
  onRemove,
  onClearAll,
}: {
  uploads: RecentUpload[];
  onRemove: (id: string) => void;
  onClearAll: () => void;
}) {
  return (
    <section className="mt-10 border-t border-gray-200 pt-6">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h2 className="text-lg font-semibold">Recent uploads</h2>
          <p className="text-xs text-gray-500">
            Persisted in this browser. The links open the matching S3 location
            in the AWS console — useful for verifying that encoding finished.
          </p>
        </div>
        <button
          type="button"
          onClick={onClearAll}
          className="text-sm text-gray-600 hover:underline"
        >
          Clear history
        </button>
      </div>
      <ul className="border border-gray-200 rounded divide-y divide-gray-200 bg-white">
        {uploads.map((u) => {
          // Older history entries may not have outputBucket; fall back to the
          // omgyes bucket for backwards compatibility with localStorage data
          // that predates the leaflet mode.
          const outputBucket = u.outputBucket || OUTPUT_BUCKET;
          const sourceUrl = s3SourceConsoleUrl(u.bucket, u.key);
          const outputUrl = s3PrefixConsoleUrl(outputBucket, u.outputPrefix);
          const eta = estimatedEncodingDoneAt(u.uploadedAt, u.size);
          const isLikelyDone = Date.now() >= eta;
          return (
            <li key={u.id} className="p-3 flex gap-3 items-start">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm break-all">
                  {u.filename}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 flex gap-2 flex-wrap items-center">
                  <span>{formatBytes(u.size)}</span>
                  <span>·</span>
                  <span>{u.mode}</span>
                  <span>·</span>
                  <span>uploaded {formatRelativeTime(u.uploadedAt)}</span>
                  <span>·</span>
                  {isLikelyDone ? (
                    <span className="text-green-700 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Encoding likely complete (check Output link)
                    </span>
                  ) : (
                    <span className="text-amber-700 flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      Encoding in progress
                    </span>
                  )}
                </div>
                <dl className="mt-1.5 text-xs grid grid-cols-[max-content_1fr] gap-x-2 gap-y-0.5">
                  <dt className="text-gray-500">Source:</dt>
                  <dd className="font-mono break-all">
                    <a
                      href={sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-700 hover:underline"
                    >
                      s3://{u.bucket}/{u.key} ↗
                    </a>
                  </dd>
                  <dt className="text-gray-500">Output:</dt>
                  <dd className="font-mono break-all">
                    <a
                      href={outputUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-700 hover:underline"
                    >
                      s3://{outputBucket}/{u.outputPrefix} ↗
                    </a>
                  </dd>
                  <dt className="text-gray-500">
                    {finalUrlLabel(u.finalUrl)}:
                  </dt>
                  <dd className="font-mono break-all">
                    <a
                      href={u.finalUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-700 hover:underline"
                    >
                      {u.finalUrl}
                    </a>
                  </dd>
                </dl>
              </div>
              <button
                type="button"
                onClick={() => onRemove(u.id)}
                title="Remove from history"
                className="flex-none p-1.5 rounded hover:bg-gray-100 text-gray-400"
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function FileRow({
  entry,
  isRunning,
  onRemove,
  onRetry,
}: {
  entry: FileEntry;
  isRunning: boolean;
  onRemove: () => void;
  onRetry: () => void;
}) {
  const { file, status } = entry;
  const sizeText = formatBytes(file.size);

  return (
    <li className="p-3 flex gap-3 items-start">
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm break-all">{file.name}</div>
        <div className="text-xs text-gray-500 mt-0.5 flex gap-2 flex-wrap items-center">
          <span>{sizeText}</span>
          <StatusBadge status={status} />
        </div>
        {status.stage === "uploading" && (
          <div className="mt-2">
            <div className="flex justify-between text-xs mb-0.5">
              <span>
                Uploading
                {status.attempt > 1 ? ` (attempt ${status.attempt})` : ""}…
              </span>
              <span>{status.pct}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded h-1.5 overflow-hidden">
              <div
                className="bg-blue-500 h-full transition-all"
                style={{ width: `${status.pct}%` }}
              />
            </div>
          </div>
        )}
        {status.stage === "error" && (
          <div className="text-xs text-red-700 mt-1 break-words">
            {status.message}
          </div>
        )}
        {status.stage === "done" && <DoneLinks status={status} />}
      </div>
      <div className="flex-none flex gap-1">
        {status.stage === "error" && status.canRetry && !isRunning && (
          <button
            type="button"
            onClick={onRetry}
            title="Retry"
            className="p-1.5 rounded hover:bg-gray-100 text-blue-600"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
        {(status.stage === "queued" ||
          status.stage === "error" ||
          status.stage === "done") &&
          !isRunning && (
            <button
              type="button"
              onClick={onRemove}
              title="Remove"
              className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
      </div>
    </li>
  );
}

function StatusBadge({ status }: { status: FileStatus }) {
  switch (status.stage) {
    case "queued":
      return <span className="text-gray-500">Queued</span>;
    case "presigning":
      return <span className="text-gray-600">Requesting upload URL…</span>;
    case "uploading":
      return <span className="text-blue-600">Uploading</span>;
    case "done":
      return (
        <span className="text-green-700 flex items-center gap-1">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Uploaded — encoding…
        </span>
      );
    case "error":
      return (
        <span className="text-red-700 flex items-center gap-1">
          <XCircle className="w-3.5 h-3.5" />
          Failed
        </span>
      );
  }
}

function DoneLinks({
  status,
}: {
  status: Extract<FileStatus, { stage: "done" }>;
}) {
  return (
    <dl className="mt-2 text-xs grid grid-cols-[max-content_1fr] gap-x-2 gap-y-0.5">
      <dt className="text-gray-500">Source:</dt>
      <dd className="font-mono break-all">
        <a
          href={s3SourceConsoleUrl(status.bucket, status.key)}
          target="_blank"
          rel="noreferrer"
          className="text-blue-700 hover:underline"
        >
          s3://{status.bucket}/{status.key} ↗
        </a>
      </dd>
      <dt className="text-gray-500">Output:</dt>
      <dd className="font-mono break-all">
        <a
          href={s3PrefixConsoleUrl(status.outputBucket, status.outputPrefix)}
          target="_blank"
          rel="noreferrer"
          className="text-blue-700 hover:underline"
        >
          s3://{status.outputBucket}/{status.outputPrefix} ↗
        </a>
      </dd>
      <dt className="text-gray-500">{finalUrlLabel(status.finalUrl)}:</dt>
      <dd className="font-mono break-all">
        <a
          href={status.finalUrl}
          target="_blank"
          rel="noreferrer"
          className="text-blue-700 hover:underline"
        >
          {status.finalUrl}
        </a>
      </dd>
    </dl>
  );
}
